import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, CameraOff, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function CameraTest() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [error, setError] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');

  const getCameraDevices = useCallback(async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
      setDevices(videoDevices);
      if (videoDevices.length > 0 && !selectedDevice) {
        setSelectedDevice(videoDevices[0].deviceId);
      }
    } catch (err) {
      console.error('Error getting devices:', err);
    }
  }, [selectedDevice]);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          deviceId: selectedDevice ? { exact: selectedDevice } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      console.log('Requesting camera with constraints:', constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true);
          console.log('Camera ready - video dimensions:', {
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight
          });
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError(`Camera error: ${err.message || err.name}`);
    }
  }, [selectedDevice, stream]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setCameraReady(false);
    }
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageUrl);
  }, []);

  const clearCapture = useCallback(() => {
    setCapturedImage(null);
  }, []);

  useEffect(() => {
    getCameraDevices();
  }, [getCameraDevices]);

  useEffect(() => {
    startCamera();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [startCamera]);

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Camera Test - AI Photo Booth</h1>
        
        {/* Device Selection */}
        {devices.length > 1 && (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Select Camera:</label>
            <select 
              value={selectedDevice} 
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="bg-slate-800 text-white px-4 py-2 rounded-lg border border-slate-700"
            >
              {devices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Status */}
        <div className="mb-6 p-4 bg-slate-900 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {cameraReady ? (
                <>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Camera Ready</span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span>Camera Initializing...</span>
                </>
              )}
            </div>
            {stream && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">
                  Active tracks: {stream.getTracks().length}
                </span>
              </div>
            )}
          </div>
          {error && (
            <div className="mt-2 text-red-400 text-sm">{error}</div>
          )}
        </div>

        {/* Camera View */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold mb-4">Live Camera</h2>
            <div className="relative bg-slate-900 rounded-lg overflow-hidden aspect-video">
              {error ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                  <CameraOff className="w-16 h-16 text-slate-600 mb-4" />
                  <p className="text-slate-400 mb-4">{error}</p>
                  <Button onClick={startCamera} className="bg-violet-600 hover:bg-violet-700">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </Button>
                </div>
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            <div className="flex gap-3 mt-4">
              <Button
                onClick={capturePhoto}
                disabled={!cameraReady}
                className="flex-1 bg-violet-600 hover:bg-violet-700"
              >
                <Camera className="w-4 h-4 mr-2" />
                Capture Photo
              </Button>
              <Button
                onClick={stopCamera}
                variant="outline"
                className="bg-slate-700 hover:bg-slate-600"
              >
                <CameraOff className="w-4 h-4 mr-2" />
                Stop
              </Button>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Captured Image</h2>
            <div className="bg-slate-900 rounded-lg overflow-hidden aspect-video">
              {capturedImage ? (
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-600">
                  No photo captured yet
                </div>
              )}
            </div>
            
            {capturedImage && (
              <Button
                onClick={clearCapture}
                variant="outline"
                className="w-full mt-4 bg-slate-700 hover:bg-slate-600"
              >
                Clear Capture
              </Button>
            )}
          </div>
        </div>

        {/* Debug Info */}
        <div className="mt-8 p-4 bg-slate-900 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Debug Information</h3>
          <div className="text-sm text-slate-400 space-y-1">
            <div>User Agent: {navigator.userAgent}</div>
            <div>Camera Devices Found: {devices.length}</div>
            <div>Selected Device: {selectedDevice || 'Default'}</div>
            <div>Stream Active: {stream ? 'Yes' : 'No'}</div>
            <div>Camera Ready: {cameraReady ? 'Yes' : 'No'}</div>
            <div>HTTPS: {window.isSecureContext ? 'Yes' : 'No'}</div>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
